import * as THREE from "three";
import $ from "jquery";
import {Racers} from "./racers/racers.js";
import {Minimap} from "./minimap/minimap.js";
import {TrackMap} from "./track/map/trackMap.js";
import {Leaderboard} from "./leaderboard/leaderboard.js";
import {LapInfo} from "./info/lapInfo.js";
import {TimeInfo} from "./info/timeInfo.js";
import {TrackProps} from "./track/props/trackProps.js";
import {Environment} from "./environment/environment.js";
import {Pointers} from "./pointers/pointers.js";
import {Sound} from "./sound/sound.js";
import {RaceDesignLocal} from "./design/raceDesignLocal.js";
import {Designed} from "./design/designed.js";
import {Globals} from "../global/globals.js";
import {RaceCameras} from "./raceCameras.js";

/**
 * The 3d race view
 */
export default class Race extends Designed {
    static FOV = 80;
    static Z_NEAR = .5;
    static Z_FAR = 12000;
    static UPDATES_PER_SECOND = 40;
    static UPDATE_RATE = 1 / Race.UPDATES_PER_SECOND;
    static FRAME_TIME_MAX = .1;
    static LEADERBOARD_DELAY = 3000;

    /**
     * Construct a race renderer
     * @param {RaceElements} elements The race elements
     * @param {RaceLoader} raceLoader The loaded race loader
     * @param {Random} random A randomizer
     */
    constructor(elements, raceLoader, random) {
        super();

        this.renderer = new THREE.WebGLRenderer({
            canvas: elements.canvas,
            antialias: true,
            alpha: true,
            preserveDrawingBuffer: true
        });

        this.renderer.autoClear = false;

        Globals.CUBE_MAP_CACHE.setRenderer(this.renderer);

        this.stop = false;
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.blips = null;
        this.track = raceLoader.track;
        this.minimap = new Minimap(new TrackMap(raceLoader.track), elements.minimap);
        this.soundEffects = true;
        this.scene = new THREE.Scene();
        this.sceneBackground = new THREE.Scene();
        this.racers = raceLoader.racers ? new Racers(this.scene, raceLoader.racers, raceLoader.track, this.finishRacer.bind(this)) : null;
        this.pointers = elements.players ? new Pointers(elements.players, this.racers) : elements.players;
        this.leaderboard = elements.leaderboard ? new Leaderboard(elements.leaderboard, this.racers, raceLoader.track['race_type']) : elements.leaderboard;
        this.lapInfo = elements.lapInfo ? new LapInfo(elements.lapInfo, this.racers.player, raceLoader.track.length, raceLoader.track.laps) : elements.lapInfo;
        this.timeInfo = elements.timeInfo ? new TimeInfo(elements.timeInfo) : elements.timeInfo;
        this.camera = new THREE.PerspectiveCamera(
            Race.FOV,
            elements.canvas.width / elements.canvas.height,
            Race.Z_NEAR,
            Race.Z_FAR);
        this.cameraBackground = this.camera.clone();
        this.environment = new Environment(this.renderer, this.scene, this.sceneBackground);
        this.cameras = new RaceCameras(this.camera, this.environment, this.track, elements.canvas, this.racers);
        this.audioPlayer = new Sound();

        this.racers?.load(this.renderer);
        this.started = false;

        this.rumblePlaying = false;
        this.ended = false;

        this.trackProps = new TrackProps(
            raceLoader.design.track,
            raceLoader.track,
            this.racers ? this.racers.trackWidth : Racers.TRACK_WIDTH_DEFAULT,
            this.scene,
            this.preRender.bind(this));

        this.startRendering();
        this.load(raceLoader.design);

        window.addEventListener("keydown", event => {
            switch (event.key) {
                case "l":
                    this.toggleSoundEffects();
            }
        });
    }

    /**
     * Unload the current design, if any
     */
    unload() {
        super.unload();

        this.environment.unload();
        this.trackProps.unload();
    }

    /**
     * Update the design
     * @param {Object} [design] An new design
     */
    load(design) {
        super.load(design);

        if (design instanceof RaceDesignLocal) {
            design.load().then(() => {
                this.environment.load(design.environment);
                this.trackProps.load(design.track);

                if (this.racers)
                    this.setBlips(this.minimap.populate(this.racers));
            });
        }
    }

    /**
     * Get the design in JSON format
     * @returns {Object} The design in JSON format
     */
    getDesign() {
        return {
            "track": this.trackProps.getDesign(),
            "environment": this.environment.getDesign()
        };
    }

    /**
     * Pre render the scene
     */
    preRender() {
        this.camera.position.set(1, 0, 0);
        this.camera.lookAt(0, 0, 0);
        this.renderer.render(this.scene, this.camera);

        this.camera.position.set(-1, 0, 0);
        this.camera.lookAt(0, 0, 0);
        this.renderer.render(this.scene, this.camera);

        this.camera.position.set(0, 0, 1);
        this.camera.lookAt(0, 0, 0);
        this.renderer.render(this.scene, this.camera);

        this.camera.position.set(0, 0, -1);
        this.camera.lookAt(0, 0, 0);
        this.renderer.render(this.scene, this.camera);
    }

    /**
     * A racer crosses the finish line
     * @param {Racer} racer The racer that finished
     * @param {boolean} last True if this is the last racer to finish
     */
    finishRacer(racer, last) {
        this.leaderboard.finish(racer);

        if (last)
            setTimeout(this.endRace.bind(this), Race.LEADERBOARD_DELAY);
            if (this.soundEffects) {
                this.audioPlayer.playSound("end_sound");
                this.audioPlayer.setVolume("end_sound", "0.2");
                this.audioPlayer.noloop("end_sound");
            }
    }

    /**
     * Start the race intro
     * @param {function} onEndIntro A function to call when the intro has ended
     */
    startIntro(onEndIntro) {
        this.cameras.startIntro(onEndIntro);
        this.pointers.makeSmall();

        this.audioPlayer.setVolume("start_sound", "0.2")
        this.audioPlayer.playSound("start_sound");
        
        if (!this.soundEffects) {
            this.audioPlayer.muteAllSounds();
        } else {
            this.audioPlayer.setVolume("start_sound", "0.2")
            this.audioPlayer.playSound("start_sound");
        }
    }
    
    /**
     * Start the race
     */
    startRace() {
        this.racers.start();
        this.cameras.endIntro();
        this.started = true;

        /** Set sound effect volume levels */
        this.audioPlayer.playSound("blast");
        this.audioPlayer.loop("ambient_bed");
        this.audioPlayer.playSound("ambient_bed");
        this.audioPlayer.loop("rumble");
        this.audioPlayer.playSound("rumble");

        if (!this.soundEffects) {
            this.audioPlayer.muteAllSounds();
        } else {
            this.audioPlayer.setVolume("blast", "0.15");
            this.audioPlayer.setVolume("ambient_bed", "0.15");
            this.audioPlayer.setVolume("rumble", "0.5");
        }

        this.checkForRumble();
    }

    /**
     * End the race
     */
    endRace() {
        this.ended = true;

        this.cameras.setEnded();

        this.checkForRumble();
        $("#raceprogress").fadeOut();
        $("#leaderboardbox").fadeOut();
        $("#minimap").fadeOut();

        // setTimeout(function() {
        //     $("#raceprogress").fadeOut();
        //     $("#leaderboardbox").fadeOut();
        //     $("#minimap").fadeOut();
        // },1000);
        
        setTimeout(function() {
            $("#finalboardbox").fadeIn(2000);
            // var fboxnum = 1;
            // $(".finish-screen-position").each(function() {
            //     fboxnum = fboxnum+1;
            // });

            // for(var z = 0;z < fboxnum;z++) {
            //     if(fboxnum <= 2) {
            //         $("#fboard"+z).fadeIn(500);
            //     } else {
            //         var fadeInTime = (z*1200)+1000;
            //         if(z > 2) {
            //             var fadeInTime = 5000;
            //             $("#fboard"+z).delay(fadeInTime).fadeIn(1500);
            //         } else {
            //             $("#fboard"+z).delay(fadeInTime).fadeIn(1500);
            //         }
            //     }
            // }
        },1000);
        

        this.audioPlayer.pauseSound("close_rumble");
        this.audioPlayer.pauseSound("rumble");
        this.racers.pauseRacecraftSounds();
    }

    /**
     * Set a different camera type
     * @param {string} name The name of the camera type
     */
    userCameraSelection(name) {
        this.cameras.userCameraSelection(name);
        
        if((name == 'Chase')||(name == 'Leader')) {
            this.rumblePlaying = true;
        } else {
            this.rumblePlaying = false;
        }
        this.checkForRumble();
    }

    /**
     * Toggle the sound effects
     */
    toggleSoundEffects() {
        this.soundEffects = !this.soundEffects;

        if ((!this.soundEffects) || (this.ended)) {
            if(this.soundEffects) {
                this.audioPlayer.setVolume("ambient_bed", "0.01");
            }
            this.audioPlayer.muteAllSounds();
            this.racers.muteRacecraftSounds();
            this.racers.pauseRacecraftSounds();
            
        } else {
            if(this.started) {
                this.racers.playRacecraftSounds();
            }
            this.racers.unmuteRacecraftSounds();
            this.audioPlayer.setVolume("start_sound", "0.2")
            this.audioPlayer.setVolume("blast", "0.15")
            this.audioPlayer.setVolume("close_rumble", "0.3");
            this.audioPlayer.setVolume("ambient_bed", "0.15");
            this.audioPlayer.setVolume("rumble", "0.4");
        }
    }

    /**
     * Trigger rumble sounds if appropriate
     */
    checkForRumble() {
        if ((this.soundEffects) && (!this.ended)) {
            if (this.rumblePlaying) {
                this.audioPlayer.playSound("close_rumble");
                this.audioPlayer.setVolume("close_rumble", "0.3");
                this.audioPlayer.loop("close_rumble");
                this.racers.muteRacecraftSounds();
                this.racers.pauseRacecraftSounds();
            } else {
                this.audioPlayer.pauseSound("close_rumble");
                this.racers.playRacecraftSounds();
                this.racers.unmuteRacecraftSounds();
            }
        } else {
            if(this.soundEffects) {
                this.audioPlayer.setVolume("ambient_bed", "0.01");
            }
            this.audioPlayer.pauseSound("close_rumble");
            this.racers.muteRacecraftSounds();
            this.racers.pauseRacecraftSounds();
        }
    }

    /**
     * Set the render target size
     * @param {number} width The width in pixels
     * @param {number} height The height in pixels
     */
    setSize(width, height) {
        this.renderer.setSize(width, height);

        this.camera.aspect = this.cameraBackground.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.cameraBackground.updateProjectionMatrix();
    }

    /**
     * Set the blips to update
     * @param {Blip[]} blips The blips to update
     */
    setBlips(blips) {
        this.blips = blips;
    }

    /**
     * Update the state
     * @param {number} delta The time delta
     */
    update(delta) {
        this.trackProps.update();
        this.racers?.update(delta);
        this.cameras.update(delta);
        this.leaderboard?.update(delta);
        this.environment.update(delta);

        if (this.started) {
            this.timeInfo.increment(delta);
            this.lapInfo.update(this.soundEffects);
        }
    }

    /**
     * Render
     * @param {number} time The time interpolation in the range [0, 1]
     */
    render(time) {
        this.trackProps.render(time);
        this.environment.render(time);
        this.racers?.render(time, this.blips);

        this.pointers?.render(
            time,
            this.camera,
            this.renderer.getContext().canvas.width,
            this.renderer.getContext().canvas.height);

        this.cameras.render(time);
        this.cameraBackground.copy(this.camera);
        this.cameraBackground.position.set(0, 0, 0);

        this.renderer.clear();
        this.renderer.render(this.sceneBackground, this.cameraBackground);
        this.renderer.clearDepth();
        this.renderer.render(this.scene, this.camera);
    }

    /**
     * Start rendering frames
     */
    startRendering() {
        let lastTime = performance.now();
        let updateTime = 0;

        const loop = time => {
            this.render(updateTime / Race.UPDATE_RATE);

            updateTime += Math.min(Race.FRAME_TIME_MAX, .001 * Math.max(0, time - lastTime));

            while (updateTime > Race.UPDATE_RATE) {
                this.update(Race.UPDATE_RATE);

                updateTime -= Race.UPDATE_RATE;
            }

            lastTime = time;

            if (!this.stop)
                requestAnimationFrame(loop);
        }

        requestAnimationFrame(loop);
    }

    /**
     * Stop rendering
     */
    stopRendering() {
        if (!this.stop) {
            this.stop = true;
            if(this.minimap) {
                this.minimap.depopulate();
            }
            if(this.leaderboard) {
                this.leaderboard.clear();
            }    
            if(this.pointers) {
                this.pointers.clear();
            }
        }
    }
}